import { Fragment } from 'react';
import { useTranslation } from 'react-i18next';
import { cn } from '@/lib/utils';
import { parseAnswer, type Inline } from './answer';
import type { ChatMessage } from './chat';

function Inlines({ inlines, onLink }: { inlines: Inline[]; onLink: (path: string) => void }) {
  return (
    <>
      {inlines.map((inline, index) => {
        if (inline.kind === 'bold') return <strong key={index}>{inline.text}</strong>;
        if (inline.kind === 'link') {
          return (
            <button key={index} type="button" onClick={() => onLink(inline.path)} className="font-medium text-primary underline-offset-2 hover:underline" data-qa-id="milo-link">
              {inline.text}
            </button>
          );
        }
        return <Fragment key={index}>{inline.text}</Fragment>;
      })}
    </>
  );
}

/** Claude's summary of how it worked the answer out: one line, opened on demand */
export function MiloThinking({ text }: { text: string }) {
  const { t } = useTranslation();
  return (
    <details className="rounded-lg border px-3 py-1.5 text-xs text-muted-foreground" data-qa-id="milo-thinking">
      <summary className="cursor-pointer font-medium select-none">{t('fleet.milo.thinkingLabel')}</summary>
      <div className="mt-1.5 whitespace-pre-wrap">{text.trim()}</div>
    </details>
  );
}

/** One answer: paragraphs and bullets, links that open a load or a truck in the app; never HTML */
export function MiloAnswer({ message, onLink }: { message: ChatMessage; onLink: (path: string) => void }) {
  return (
    <div className={cn('space-y-2 text-sm leading-relaxed', message.failed && 'text-muted-foreground')} data-qa-id="milo-answer">
      {message.thinking?.trim() && <MiloThinking text={message.thinking} />}
      {parseAnswer(message.text, message.refs).map((block, index) =>
        block.kind === 'paragraph' ? (
          <p key={index}>
            <Inlines inlines={block.inlines} onLink={onLink} />
          </p>
        ) : (
          <ul key={index} className="list-disc space-y-1 pl-5">
            {block.items.map((item, itemIndex) => (
              <li key={itemIndex}>
                <Inlines inlines={item} onLink={onLink} />
              </li>
            ))}
          </ul>
        )
      )}
    </div>
  );
}
